import { haversineMiles } from '@utils/distance.ts'
import type { Activity } from '@hewey/shared'
import type { ActivityFilter } from '../filter.ts'
import type { TodayWeather } from '../weather.ts'

// A suggested-collection row for the Explore landing. `seeAll`, when present,
// is the filter patch that reproduces the collection in the results grid —
// tapping "See all" applies it and drops the user into the grid. Recency-based
// rows have no filter equivalent, so they omit it.
export type Collection = {
	key: string
	title: string
	subtitle?: string
	activities: Activity[]
	seeAll?: Partial<ActivityFilter>
}

// Everything a builder might need. Nearby/weather builders return null when the
// inputs they require (location, forecast) aren't available yet.
export type BuilderCtx = {
	activities: Activity[]
	origin: { lat: number; lng: number } | null
	weather: TodayWeather | null
	// Short locality label for the origin (e.g. "Bursledon"), or null until
	// reverse-geocoded. Only the weather row uses it.
	placeName: string | null
}

export type CollectionBuilder = (ctx: BuilderCtx) => Collection | null

// Per-row cap; rows are horizontal scrollers two cards tall, so a dozen is
// plenty before "See all" earns its keep.
export const CAP = 12

export const byCreatedDesc = (a: Activity, b: Activity): number =>
	b.createdAt.localeCompare(a.createdAt)

export type Placed = { activity: Activity; miles: number }

// Place activities paired with their distance (miles) from origin, nearest
// first. Home activities have no coordinates, so they drop out.
export const placeDistances = (
	activities: Activity[],
	origin: { lat: number; lng: number },
): Placed[] =>
	activities
		.flatMap((a) =>
			a.location.kind === 'place'
				? [
						{
							activity: a,
							miles: haversineMiles(
								origin.lat,
								origin.lng,
								a.location.lat,
								a.location.lng,
							),
						},
					]
				: [],
		)
		.sort((x, y) => x.miles - y.miles)
